<script setup lang="ts">
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    reactive,
    ref,
    toRef,
    watch,
    watchEffect
  } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { ChevronDown, ChevronsDownUp, ChevronUp, Plus, Trash2 } from 'lucide-vue-next'
  import {
    PlusOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    HolderOutlined,
    CompressOutlined,
    ExpandOutlined
  } from '@ant-design/icons-vue'
  import { CustomReportField } from '../models/CustomReportField'
  import { CustomReportsService } from '../services/CustomReportsService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import CustomReportConfiguration from './CustomReportConfiguration.vue'
  import { CustomReportSchema } from '../models/CustomReportSchema'
  import { CustomReport } from '../models/CustomReport'
  import { CustomReportFieldType } from '../models/CustomReportFieldType'
  import { PostProcessingOutputItem } from '@/models/PostProcessingOutputItem'
  import PostProcessingOutputSelectionModal from '@/shared/components/PostProcessingOutputSelectionModal.vue'
  import { PostProcessingOutput } from '@/models/PostProcessingOutput'
  import { PostProcessingOutputPlot } from '@/models/PostProcessingOutputPlot'
  import { PostProcessingOutputTable } from '@/models/PostProcessingOutputTable'
  import { useSortedOutputs } from '../services/useSortedCustomOutputs'

  interface IArrayCustomReportBuilderProps {
    customReportSchemas: Array<CustomReportSchema>
    customReportField: CustomReportField
    customPlotsOutput?: PostProcessingOutput<PostProcessingOutputPlot>
    customTablesOutput?: PostProcessingOutput<PostProcessingOutputTable>
    modelValue: []
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
  }

  interface IArrayCustomReportBuilderEmits {
    (e: 'update:modelValue', value: Array<any>): void
    (
      e: 'child-customReport-type-changed',
      type: string,
      customReport: CustomReport,
      configuration: {}
    ): void
  }

  const props = defineProps<IArrayCustomReportBuilderProps>()
  const emit = defineEmits<IArrayCustomReportBuilderEmits>()

  const arrayCustomReportField = reactive<CustomReportField>(props.customReportField)

  if (arrayCustomReportField.field_type !== CustomReportFieldType.Array)
    throw new Error('ArrayCustomReportBuilder requires an Array type CustomReportField!')
  if (arrayCustomReportField.item_schema == null)
    throw new Error('Array type CustomReportFields require an item_schema!')

  const customReports = ref<Array<CustomReport>>([])

  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  const sortedPlots = useSortedOutputs(toRef(props, 'customPlotsOutput'))
  const sortedTables = useSortedOutputs(toRef(props, 'customTablesOutput'))

  const showPostProcessingOutputSelection = ref<boolean>(false)
  const postProcessingOutputs = computed(() => [...sortedPlots.value, ...sortedTables.value])

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // Drag / reorder state
  const draggedIndex = ref<number | null>(null)
  const dragOverIndex = ref<number | null>(null)
  const instanceId = `arrayCustomReport-${Math.random().toString(36).slice(2)}`

  const moveItem = (from: number, to: number) => {
    if (from === to) return
    const item = customReports.value.splice(from, 1)[0]
    customReports.value.splice(to, 0, item)
  }

  const moveUp = (index: number) => {
    if (index <= 0) return
    moveItem(index, index - 1)
  }

  const moveDown = (index: number) => {
    if (index >= customReports.value.length - 1) return
    moveItem(index, index + 1)
  }

  const handleDragLeave = () => {
    dragOverIndex.value = null
  }

  const handleDragStart = (event: DragEvent, index: number) => {
    event.stopPropagation()
    draggedIndex.value = index
    const id = instanceId
    // store origin in dataTransfer and window fallback
    try {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
        event.dataTransfer.setData('application/x-array-builder-id', id)
      }
    } catch {}
    ;(window as any).__arrayBuilderDragSourceId = id
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null

    if (sourceId !== instanceId) {
      dragOverIndex.value = -1
      return
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    let sourceId: string | null = null
    try {
      sourceId = event.dataTransfer?.getData('application/x-array-builder-id') || null
    } catch {}
    sourceId = sourceId || (window as any).__arrayBuilderDragSourceId || null
    if (sourceId !== instanceId) return
    if (draggedIndex.value == null) return
    const from = draggedIndex.value
    const to = targetIndex
    if (from === to) {
      draggedIndex.value = null
      dragOverIndex.value = -1
      ;(window as any).__arrayBuilderDragSourceId = null
      return
    }
    moveItem(from, to)
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = -1
    ;(window as any).__arrayBuilderDragSourceId = null
  }

  // External (post-processing) drag state for container-level drops
  const externalDragOver = ref<boolean>(false)
  const externalContainerDragOver = ref<boolean>(false)

  // index of the mapping row currently hovered by an external drag (-1 for placeholder / before first)
  const externalDragOverIndex = ref<number | null>(null)
  const containerRef = ref<HTMLElement | null>(null)
  const lastDragClient = ref<{ x: number; y: number } | null>(null)

  const handleContainerExternalDragOver = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.configurationLevel == null || props.configurationLevel !== 2) return

    if (isExternalPostProcessingDragOver(event)) {
      externalContainerDragOver.value = true
      attachGlobalMouseListeners()
      lastDragClient.value = { x: event.clientX, y: event.clientY }
    }
  }

  const handleContainerExternalDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.configurationLevel == null || props.configurationLevel !== 2) return
    if (!containerRef.value) {
      externalContainerDragOver.value = false
      detachGlobalMouseListeners()
      return
    }

    // prefer the event coords, fall back to lastDragClient
    const x = (event.clientX || lastDragClient.value?.x) ?? -1
    const y = (event.clientY || lastDragClient.value?.y) ?? -1

    if (x < 0 || y < 0) {
      // if coords unavailable, clear conservatively
      externalContainerDragOver.value = false
      detachGlobalMouseListeners()
      return
    }

    const el = document.elementFromPoint(x, y)
    if (!el || !containerRef.value.contains(el)) {
      externalContainerDragOver.value = false
      detachGlobalMouseListeners()
    }
  }

  const isExternalPostProcessingDragOver = (event: DragEvent): boolean => {
    let json = ''
    try {
      json =
        event.dataTransfer?.getData('application/json') ||
        event.dataTransfer?.getData('text/plain') ||
        (window as any).__postProcessingDragPayloadJson ||
        ''
      if (!json) return false
      const parsed = JSON.parse(json)
      if (parsed?.source === 'postprocessing' && parsed?.item) {
        return true
      }
    } catch {}
    return false
  }

  const handleExternalDragOver = (event: DragEvent, insertIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.configurationLevel == null || props.configurationLevel !== 2) return

    if (isExternalPostProcessingDragOver(event)) {
      externalDragOver.value = true
      externalDragOverIndex.value = insertIndex
    }
  }

  const handleExternalDragLeave = (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    externalDragOver.value = false
    externalDragOverIndex.value = null
  }

  const handleExternalContainerDrop = (event: DragEvent, insertIndex: number) => {
    event.preventDefault()
    event.stopPropagation()
    lastDragClient.value = null
    externalContainerDragOver.value = false
    detachGlobalMouseListeners()
    if (props.configurationLevel == null || props.configurationLevel !== 2) return
    externalDragOver.value = false
    externalDragOverIndex.value = null

    let json = ''
    try {
      json =
        event.dataTransfer?.getData('application/json') ||
        event.dataTransfer?.getData('text/plain') ||
        (window as any).__postProcessingDragPayloadJson ||
        ''
      if (!json) return
      const parsed = JSON.parse(json)
      if (parsed?.source === 'postprocessing' && parsed?.item) {
        // If we hovered a specific row, insert the new item just below it.
        if (insertIndex != null && insertIndex >= 0) {
          onPostProcessingOutputDropped(parsed.item as any, insertIndex)
        } else {
          // placeholder or no specific row -> insert at start
          onPostProcessingOutputDropped(parsed.item as any, 0)
        }
      }
    } catch {}
  }

  const globalMouseListenersAttached = ref(false)

  const resetExternalDragState = () => {
    lastDragClient.value = null
    externalContainerDragOver.value = false
    externalDragOver.value = false
    externalDragOverIndex.value = null
  }

  //@ts-ignore
  const onGlobalMouseUp = (e: Event) => {
    resetExternalDragState()
    detachGlobalMouseListeners()
  }

  const attachGlobalMouseListeners = () => {
    if (globalMouseListenersAttached.value) return
    window.addEventListener('pointerup', onGlobalMouseUp, true)
    window.addEventListener('mouseup', onGlobalMouseUp, true)
    window.addEventListener('dragend', onGlobalMouseUp, true)
    window.addEventListener('blur', onGlobalMouseUp)
    globalMouseListenersAttached.value = true
  }

  const detachGlobalMouseListeners = () => {
    if (!globalMouseListenersAttached.value) return
    window.removeEventListener('pointerup', onGlobalMouseUp, true)
    window.removeEventListener('mouseup', onGlobalMouseUp, true)
    window.removeEventListener('dragend', onGlobalMouseUp, true)
    window.removeEventListener('blur', onGlobalMouseUp)
    globalMouseListenersAttached.value = false
  }

  watch(
    props.modelValue,
    (newVal) => {
      customReports.value = []
      if (Array.isArray(newVal)) {
        newVal.forEach((value: any) => {
          const customReport = CustomReportsService.createCustomReportFromCustomReportField(
            arrayCustomReportField.item_schema!
          )
          customReport.configuration =
            value.configuration != null
              ? CustomReportsService.getFlattenedCustomReport(value, null)
              : value
          customReports.value.push(customReport)
        })
      }
    },
    { immediate: true, deep: true }
  )

  watch(
    customReports,
    (newValue) => {
      emit('update:modelValue', newValue)
    },
    { deep: true }
  )

  const initializeConfiguration = (
    customReport: CustomReport,
    fields: Array<CustomReportField>
  ) => {
    if (customReport == null) throw new Error('customReport is required')
    if (arrayCustomReportField.item_schema?.field_type !== CustomReportFieldType.Object) return
    const config = CustomReportsService.initializeCustomReportFieldConfiguration(fields)
    customReport.configuration = config
  }

  // Code will only allow arraybuilders with configurationLevel 2 or greater to accept item drops
  const onPostProcessingOutputDropped = (
    items: PostProcessingOutputItem | Array<PostProcessingOutputItem>,
    insertIndex?: number
  ): void => {
    if (items != null) {
      const _items = Array.isArray(items) ? items : [items]
      _items.forEach((value: PostProcessingOutputItem) => {
        // This will be the report.section.item schema
        const newCustomReportItem = CustomReportsService.createCustomReportFromCustomReportField(
          arrayCustomReportField.item_schema!
        )
        initializeConfiguration(
          newCustomReportItem,
          arrayCustomReportField.item_schema?.fields || []
        )
        // Custom override to prepopulate added item title value
        value.title = value.id
        Object.assign(newCustomReportItem.configuration, value)

        // If an insert index was provided, insert at that position. Otherwise append.
        if (
          typeof insertIndex === 'number' &&
          insertIndex >= 0 &&
          insertIndex <= customReports.value.length
        ) {
          customReports.value.splice(insertIndex, 0, newCustomReportItem)
        } else {
          customReports.value.push(newCustomReportItem)
        }
      })
      message.success(
        `Added custom report(s): ${arrayCustomReportField.display_name}`,
        defaultMessageDurationSeconds
      )
    }
  }

  const scrollToLastCustomReport = () => {
    const container = containerRef.value
    if (!container) return

    const mappingContainer = container.querySelector(
      '.customReport-mapping-row-container'
    ) as HTMLElement | null

    if (!mappingContainer) {
      container.scrollTop = container.scrollHeight
      return
    }

    const directRows = Array.from(mappingContainer.children)
      .map((child) => (child as HTMLElement).querySelector('.customReport-mapping-row'))
      .filter((r): r is HTMLElement => !!r)
    const lastRow = directRows.length ? directRows[directRows.length - 1] : null

    if (lastRow) {
      lastRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    } else {
      mappingContainer.scrollTop = mappingContainer.scrollHeight
    }
  }

  const onPostProcessingOutputSelectionVisiblityChanged = (visible: boolean) => {
    if (visible == false) {
      showPostProcessingOutputSelection.value = false
      emit('update:modelValue', customReports.value)
      nextTick(() => {
        // small timeout can help if transitions are used
        setTimeout(() => {
          scrollToLastCustomReport()
        }, 0)
      })
    }
  }

  const onPostProcessingOutputItemSelected = (items: Array<PostProcessingOutputItem>) => {
    if (!items?.length) return
    onPostProcessingOutputDropped(items, customReports.value?.length ?? 0)
  }

  const isAddDisabled = computed(() => {
    const maxItems = (arrayCustomReportField.field_type_args as any)?.array_max_items
    if (maxItems === undefined || maxItems === null) return false
    if (typeof maxItems !== 'number' || isNaN(maxItems) || maxItems < 0) return false
    return customReports.value.length >= maxItems
  })

  const handleAddCustomReport = () => {
    if (props.configurationLevel === 2) {
      showPostProcessingOutputSelection.value = true
    } else {
      addCustomReport()
    }
  }

  const addCustomReport = () => {
    const newCustomReport = CustomReportsService.createCustomReportFromCustomReportField(
      arrayCustomReportField.item_schema!
    )
    initializeConfiguration(newCustomReport, arrayCustomReportField.item_schema?.fields || [])
    customReports.value.push(newCustomReport)
    message.success(
      `Added custom report: ${arrayCustomReportField.display_name}`,
      defaultMessageDurationSeconds
    )
    nextTick(() => {
      // small timeout can help if transitions are used
      setTimeout(scrollToLastCustomReport, 0)
    })

    return newCustomReport
  }

  const removeCustomReport = (index: number) => {
    customReports.value.splice(index, 1)
  }

  const handleConfigurationUpdate = (
    customReport: CustomReport,
    config: Record<string, any>,
    customReportTypeChange?: boolean
  ) => {
    const customReportIndex = customReports.value.findIndex((c) => c.id === customReport.id)
    if (customReportIndex > -1) {
      customReports.value[customReportIndex].configuration = { ...config }
    }
    if (customReportTypeChange === true) {
      emit('child-customReport-type-changed', customReport.type, customReport, { ...config })
    }
  }

  onBeforeUnmount(() => detachGlobalMouseListeners())
</script>

<template>
  <div
    ref="containerRef"
    @dragover.prevent.stop="handleContainerExternalDragOver($event)"
    @dragleave.prevent.stop="handleContainerExternalDragLeave($event)"
    v-if="arrayCustomReportField.item_schema?.field_type === CustomReportFieldType.Object"
    class="customReport-array-object-builder-container"
  >
    <div class="mt-[-40px] flex items-center gap-2 self-end">
      <a-tooltip placement="top">
        <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
        <Button
          style="padding: 3px; border: none"
          type="text"
          size="large"
          class="flex items-center justify-center"
          @click="toggleGlobalCollapse"
          :disabled="customReports.length === 0"
          :aria-disabled="customReports.length === 0"
          aria-label="Toggle Collapse All"
        >
          <CompressOutlined
            v-if="isCollapsed"
            class="text-base"
            style="line-height: 1; margin: 0"
          />
          <ExpandOutlined v-else class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>

      <a-tooltip placement="top">
        <template #title>{{ `Add ${arrayCustomReportField.item_schema!.display_name}` }}</template>
        <Button
          type="primary"
          shape="circle"
          size="large"
          class="flex items-center justify-center"
          @click="handleAddCustomReport"
          :disabled="isAddDisabled"
          :aria-disabled="isAddDisabled"
        >
          <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
        </Button>
      </a-tooltip>
    </div>

    <div class="customReport-mapping-row-container mt-5">
      <template v-if="props.configurationLevel === 2 && customReports.length === 0">
        <div
          class="drop-insert-zone flex items-center justify-center"
          @dragover.prevent.stop="handleExternalDragOver($event, 0)"
          @dragleave.prevent.stop="handleExternalDragLeave($event)"
          @drop.prevent.stop="handleExternalContainerDrop($event, 0)"
          :class="{
            active: externalDragOver && externalDragOverIndex === 0,
            activeDrag: true,
            static: true
          }"
          style="margin-left: 5px; margin-right: 5px; height: 40px"
        >
          <Plus
            class="drop-insert-zone-icon"
            :class="{ active: true }"
            style="line-height: 1; margin: 0"
          />
        </div>
      </template>

      <template v-if="props.configurationLevel === 2">
        <div
          class="drop-insert-zone flex justify-center"
          v-if="customReports.length > 0"
          @dragover.prevent.stop="handleExternalDragOver($event, 0)"
          @dragleave.prevent.stop="handleExternalDragLeave($event)"
          @drop.prevent.stop="handleExternalContainerDrop($event, 0)"
          :class="{
            active: externalDragOver && externalDragOverIndex === 0,
            activeDrag: externalContainerDragOver
          }"
          style="margin-left: 5px; margin-right: 5px"
        >
          <ChevronDown
            class="drop-insert-zone-icon"
            :class="{ active: externalDragOver && externalDragOverIndex === 0 }"
            style="line-height: 0.8; margin: 0; font-size: 8px"
          />
        </div>
      </template>

      <div
        style="margin-left: 10px; margin-right: 10px"
        v-for="(customReport, index) in customReports"
        :key="customReport.id"
      >
        <!-- Render a dropzone before the first item, between each item, and after the last item -->
        <div
          class="customReport-mapping-row"
          :data-index="index"
          @dragover="handleDragOver($event, index)"
          @dragleave="handleDragLeave"
          @drop="handleDrop($event, index)"
          :class="{
            'drop-zone-active': dragOverIndex === index
          }"
        >
          <div
            class="drag-handle"
            draggable="true"
            @dragstart="handleDragStart($event, index)"
            @dragend="handleDragEnd"
            tabindex="0"
            role="button"
            :aria-grabbed="draggedIndex === index"
            aria-label="Drag to reorder"
          >
            <HolderOutlined class="text-base" style="line-height: 1; margin: 0" />
          </div>

          <CustomReportConfiguration
            :configuration-level="props.configurationLevel + 1"
            :custom-report="customReport"
            :custom-plots-output="customPlotsOutput"
            :custom-tables-output="customTablesOutput"
            @configuration-updated="handleConfigurationUpdate"
            :custom-report-schemas="customReportSchemas"
            :is-condensed-layout="props.isCondensedLayout"
            :global-collapsed="globalCollapsed"
          />

          <div class="action-buttons ml-2 flex flex-col items-center">
            <a-tooltip placement="left">
              <template #title>Move Up</template>
              <Button
                type="default"
                shape="circle"
                size="small"
                class="compact-action-btn"
                @click="moveUp(index)"
                :disabled="index === 0"
                :aria-disabled="index === 0"
              >
                <ArrowUpOutlined class="compact-icon" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip placement="left">
              <template #title>
                {{ `Remove ${arrayCustomReportField.item_schema!.display_name}` }}
              </template>
              <Button
                type="default"
                shape="circle"
                size="small"
                class="compact-action-btn ant-btn-dangerous my-1"
                @click="removeCustomReport(index)"
              >
                <Trash2 class="compact-icon" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>

            <a-tooltip placement="left">
              <template #title>Move Down</template>
              <Button
                type="default"
                shape="circle"
                size="small"
                class="compact-action-btn"
                @click="moveDown(index)"
                :disabled="index === customReports.length - 1"
                :aria-disabled="index === customReports.length - 1"
              >
                <ArrowDownOutlined class="compact-icon" style="line-height: 1; margin: 0" />
              </Button>
            </a-tooltip>
          </div>
        </div>

        <!-- insert zone after this item -->
        <template v-if="props.configurationLevel === 2">
          <div
            class="drop-insert-zone align-center flex justify-center"
            @dragover.prevent.stop="handleExternalDragOver($event, index + 1)"
            @dragleave.prevent.stop="handleExternalDragLeave($event)"
            @drop.prevent.stop="handleExternalContainerDrop($event, index + 1)"
            :class="{
              active: externalDragOver && externalDragOverIndex === index + 1,
              activeDrag: externalContainerDragOver
            }"
          >
            <ChevronsDownUp
              v-if="index + 1 > 0 && index + 2 <= customReports.length"
              class="drop-insert-zone-icon"
              :class="{ active: externalDragOver && externalDragOverIndex === index + 1 }"
              style="line-height: 1; margin: 0"
            />
            <ChevronUp
              v-else
              class="drop-insert-zone-icon"
              :class="{ active: externalDragOver && externalDragOverIndex === index + 1 }"
              style="line-height: 1; margin: 0"
            />
          </div>
        </template>
      </div>
    </div>
  </div>

  <!-- PostProcessing Selection Modal -->
  <PostProcessingOutputSelectionModal
    v-if="configurationLevel === 2"
    v-model:visible="showPostProcessingOutputSelection"
    @update:visible="onPostProcessingOutputSelectionVisiblityChanged"
    :post-processing-outputs="postProcessingOutputs"
    @post-processing-output-selected="onPostProcessingOutputItemSelected"
  />
</template>

<style scoped>
  .customReport-array-object-builder-container {
    display: flex;
    flex-direction: column;
  }

  .customReport-mapping-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 5px;
    margin-bottom: 5px;
  }

  .drag-handle {
    cursor: grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground, #6b7280);
  }

  .compact-action-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .compact-action-btn.compact-action-btn.ant-btn-dangerous {
    padding: 0;
  }

  .compact-icon {
    display: inline-flex;
  }

  /* Ensure SVG-based icons (lucide) respect compact sizing */
  .compact-icon,
  .compact-icon svg {
    width: 14px !important;
    height: 14px !important;
    line-height: 1 !important;
  }

  .customReport-mapping-row.drop-zone-active {
    outline: 2px dashed rgba(24, 144, 255, 0.5);
    background: rgba(24, 144, 255, 0.03);
  }

  .drop-insert-zone {
    height: 26px;
    margin: 3px 0;
    transition:
      background-color 120ms ease,
      outline 120ms ease;
    pointer-events: auto;
  }

  @keyframes dropBorderPulseOut {
    0% {
      border-top-color: rgba(24, 144, 255, 1);
      border-bottom-color: rgba(24, 144, 255, 1);
    }
    25% {
      border-top-color: rgba(24, 144, 255, 0.5);
      border-bottom-color: rgba(24, 144, 255, 0.5);
    }
    50% {
      border-top-color: rgba(24, 144, 255, 1);
      border-bottom-color: rgba(24, 144, 255, 1);
    }
    75% {
      border-top-color: rgba(24, 144, 255, 0.5);
      border-bottom-color: rgba(24, 144, 255, 0.5);
    }
    100% {
      border-top-color: rgba(24, 144, 255, 0.2);
      border-bottom-color: rgba(24, 144, 255, 0.2);
    }
  }

  .drop-insert-zone.static {
    animation: dropBorderPulseOut 2000ms ease-in-out forwards;
  }

  .drop-insert-zone.activeDrag {
    background: rgba(24, 143, 255, 0.007);
    border-top: 2px dashed rgba(24, 144, 255, 0.2);
    border-bottom: 2px dashed rgba(24, 144, 255, 0.2);
  }

  @keyframes dropBorderPulse {
    0% {
      border-top-color: rgba(24, 144, 255, 0.5);
      border-bottom-color: rgba(24, 144, 255, 0.5);
    }
    25% {
      border-top-color: rgba(24, 144, 255, 1);
      border-bottom-color: rgba(24, 144, 255, 1);
    }
    50% {
      border-top-color: rgba(24, 144, 255, 0.5);
      border-bottom-color: rgba(24, 144, 255, 0.5);
    }
    75% {
      border-top-color: rgba(24, 144, 255, 1);
      border-bottom-color: rgba(24, 144, 255, 1);
    }
    100% {
      border-top-color: rgba(24, 144, 255, 1);
      border-bottom-color: rgba(24, 144, 255, 1);
    }
  }

  .drop-insert-zone.active {
    border-top: 2px dashed rgba(24, 144, 255, 0.5);
    border-bottom: 2px dashed rgba(24, 144, 255, 0.5);
    background: rgba(24, 144, 255, 0.03);
    animation: dropBorderPulse 1200ms ease-in-out forwards;
  }

  .drop-insert-zone-icon {
    transition:
      background-color 120ms ease,
      outline 120ms ease;
    color: rgba(24, 144, 255, 0.03);
  }

  .drop-insert-zone-icon.active {
    color: rgba(24, 144, 255, 0.5);
  }

  .action-buttons .ant-btn {
    padding: 3px;
  }

  select {
    padding: 4px;
  }
</style>
